import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  LinearProgress,
  Paper,
} from "@mui/material";
import { Close, Image as ImageIcon } from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import useStyles from "./styles";
import CropperPanel from "./CropperPainel";
import { DropZone } from "./DropZone";
import { FilePreview } from "./filePreview";
import { useFileManagement, useImageCrop } from "./hooks";
import { cropImageCenter } from "./functions";

// Constantes e utilitários
const MAX_FILES_DEFAULT = 10;
const MAX_SIZE_MB_DEFAULT = 1; // 1MB
const sizeOutput = 150;

export const ImageUploadModal = ({
  open,
  onClose,
  onUpload,
  maxFiles = MAX_FILES_DEFAULT,
  maxSizeMB = 0,
  api,
  icon,
  setSnackbar = () => {},
  setIconElement = () => {},
  setIconsElement = () => {},
  iconsElement = [],
}) => {
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [sizeFull, setSizeFull] = useState(0);
  const iconsElementNames = useMemo(
    () => iconsElement.map((icon) => icon.nome),
    [iconsElement]
  );
  const {
    files,
    setFiles,
    imgSelected,
    setImgSelected,
    handleFiles,
    removeFile,
    checkFileError,
  } = useFileManagement(
    iconsElementNames,
    icon,
    sizeOutput,
    MAX_SIZE_MB_DEFAULT,
    sizeFull
  );
  const validateError = useMemo(() => files.some((f) => f.error), [files]);

  const { crop, setCrop, zoom, setZoom, getCroppedImage, resetCrop } =
    useImageCrop(sizeOutput);

  const styles = useStyles(theme, imgSelected, isDragging);

  // Efeito para configurar modo de edição quando há um ícone
  useEffect(() => {
    if (icon) {
      setImgSelected({
        src: icon.img,
        name: icon.nome,
        croppedAreaPixels: null,
        id: icon.id,
      });
      setEditMode(true);
      return;
    }
    setEditMode(false);
    setImgSelected(null);
  }, [icon, setImgSelected, open]);

  useEffect(() => {
    setImgSelected(null);
    if (!files.length) return;
    const totalBytes = files.reduce((sum, f) => sum + (f.file?.size || 0), 0);
    const totalKB = totalBytes / 1024;
    const totalMB = totalKB / 1024;
    setSizeFull(totalMB);
  }, [files, maxSizeMB, maxFiles]);

  // Handlers de drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileInput = (e) => handleFiles(e.target.files);

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (checkFileError(icon?.name)) return;

    setUploading(true);
    const logs = JSON.parse(localStorage.getItem("logs"));

    try {
      const response = await api.post("easyMaps/img/create", {
        files: await Promise.all(
          files.map(async (f) => {
            const croppedImage = await cropImageCenter(f.src, sizeOutput);
            return {
              nome: f.name,
              img: croppedImage.src,
            };
          })
        ),
        usuarioClienteId: logs.usuarioClienteId,
        logsLoginClienteId: logs.id,
      });

      if (response.data.status === "Error") {
        setSnackbar({
          children: "Error: Não foi possível criar os ícones",
          severity: "error",
        });
      } else {
        setIconsElement((prev) => [...prev, ...response.data]);
        setSnackbar({
          children: "Icones adicionados com sucesso",
          severity: "success",
        });
      }
      handleClose();
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Error: Não foi possível se conectar com o servidor",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async (iconData) => {
    if (checkFileError(iconData.name)) return;

    const logs = JSON.parse(localStorage.getItem("logs"));

    try {
      const response = await api.put("easyMaps/img/update", {
        id: iconData.id,
        nome: iconData.name,
        img: iconData.src,
        usuarioClienteId: logs.usuarioClienteId,
        logsLoginClienteId: logs.id,
      });

      if (response.data.status === "Error") {
        setSnackbar({
          children: "Error: Não foi possível atualizar os ícones",
          severity: "error",
        });
      } else {
        const data = {
          nome: iconData.name,
          id: response.data.id,
          img: iconData.src,
        };
        setIconElement(data);
        setIconsElement((icons) =>
          icons.map((icon) => (icon.id === data.id ? data : icon))
        );
        setSnackbar({
          children: "Icone atualizado com sucesso",
          severity: "success",
        });
        handleClose();
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Error: Não foi possível se conectar com o servidor",
        severity: "error",
      });
    }
  };

  const handleClose = () => {
    onClose();
    setFiles([]);
    setIsDragging(false);
    setUploading(false);
    setImgSelected(null);
    resetCrop();
    setEditMode(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: styles.dialogPaper,
      }}
    >
      {/* Painel principal */}
      {!editMode && open && (
        <Paper sx={styles.mainPanel}>
          <DialogTitle sx={styles.dialogTitle}>
            <Box sx={styles.titleBox}>
              <ImageIcon />
              <Typography variant="h6">Upload de Imagens</Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ mt: 2 }}>
            <DropZone
              isDragging={isDragging}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              handleFileInput={handleFileInput}
              maxFiles={maxFiles}
              maxSizeMB={maxSizeMB}
              theme={theme}
              styles={styles}
              sizeFull={sizeFull}
              MAX_SIZE_MB_DEFAULT={MAX_SIZE_MB_DEFAULT}
            />

            {files.length > 0 && (
              <FilePreview
                files={files}
                removeFile={removeFile}
                setImgSelected={setImgSelected}
                styles={styles}
                sizeFull={sizeFull}
                MAX_SIZE_MB_DEFAULT={MAX_SIZE_MB_DEFAULT}
              />
            )}

            {uploading && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
                <Typography variant="caption" sx={styles.progressCaption}>
                  Fazendo upload...
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={styles.dialogActions}>
            <Button onClick={handleClose} disabled={uploading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={files.length === 0 || uploading || validateError}
            >
              Fazer Upload
            </Button>
          </DialogActions>
        </Paper>
      )}

      {/* Painel lateral - Cropper */}
      {imgSelected?.src && open && (
        <CropperPanel
          imgSelected={imgSelected}
          setImgSelected={setImgSelected}
          crop={crop}
          setCrop={setCrop}
          zoom={zoom}
          setZoom={setZoom}
          getCroppedImage={getCroppedImage}
          checkFileError={checkFileError}
          editMode={editMode}
          handleEdit={handleEdit}
          setFiles={setFiles}
          styles={styles}
          theme={theme}
          handleClose={handleClose}
        />
      )}
    </Dialog>
  );
};
